import { useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import type { IReplayEvent, IReplayFrame } from '@interfaces/IMonitorApi';
import { useAppSelector } from '@store/hooks';
import {
  selectFloorChips,
  selectManagerColorByTagId,
  selectManagers,
  type ManagerRecord,
  selectProfessions,
  selectSelectedEquipmentTypes,
  selectSelectedManagerIds,
  selectSelectedProfessionIds,
  type FloorWithChips,
} from '@store/selectors/monitor.selectors';
import {
  buildReplayView,
  replayFilters,
  type IReplayFilters,
  type IReplayView,
  type ReplayEquipment,
} from './buildReplayView';
import {
  replayEquipmentRows,
  replayManagerRows,
  replaySafetyRows,
  replayWorkforceRows,
  type ReplaySafetyEvent,
} from './replayRail';

/** While the day loads, its equipment dictionary is empty — one shared object, so the view keeps its identity. */
const NO_EQUIPMENT: ReplayEquipment = Object.freeze({});

/** What the replay borrows from a live floor: its label and flags (never its live counts). */
const floorLabelsKey = (floors: FloorWithChips[]) =>
  floors.map((floor) => [floor.floorId, floor.label, floor.isSpecial, floor.isHoist, floor.isMovingFloor].join('|')).join('\n');
const sameFloorLabels = (before: FloorWithChips[], after: FloorWithChips[]) =>
  floorLabelsKey(before) === floorLabelsKey(after);

const sameColors = (before: ReadonlyMap<string, string>, after: ReadonlyMap<string, string>) =>
  before.size === after.size && [...before].every(([tagId, color]) => after.get(tagId) === color);

/** The LeftRail filters (the selectors live reads) in the replay's terms. */
const selectReplayFilters = createSelector(
  [selectSelectedProfessionIds, selectSelectedManagerIds, selectManagers, selectSelectedEquipmentTypes],
  replayFilters,
);
/** Filters compared by value: a live manager-list update that resolves the same names changes nothing. */
const filtersKey = (filters: IReplayFilters) =>
  [filters.professionIds, filters.managerNames, filters.equipmentTypes]
    .map((allowed) => (allowed ? [...allowed].join(',') : '*'))
    .join('|');
const sameFilters = (before: IReplayFilters, after: IReplayFilters) => filtersKey(before) === filtersKey(after);

/** What a manager row borrows from the site's manager list, compared by value. */
const managersKey = (managers: readonly ManagerRecord[]) =>
  managers.map((manager) => [manager._id, manager.name, manager.tradeGroup, manager.backgroundColor].join('|')).join('\n');
// Runs on every store update: the reference check spares the string build when nothing changed.
const sameManagers = (before: readonly ManagerRecord[], after: readonly ManagerRecord[]) =>
  before === after || managersKey(before) === managersKey(after);

/**
 * The building and Live Summary at the replay frame (null while live). It depends
 * on the frame and the static lookups only — the live floors lend their labels and
 * the on-site managers their colours, and the manager list resolves the filters —
 * each compared by value, so a live stream message that leaves them unchanged
 * does not rebuild the view.
 */
export function useReplayView(
  frame: IReplayFrame | null,
  replaying: boolean,
  equipment: ReplayEquipment | undefined,
  activeEvents: readonly IReplayEvent[],
  /** The day's safety alerts the playhead has reached; keep it referentially stable. */
  safety: readonly ReplaySafetyEvent[],
): IReplayView | null {
  const liveFloors = useAppSelector(selectFloorChips, sameFloorLabels);
  const managerColors = useAppSelector(selectManagerColorByTagId, sameColors);
  const professions = useAppSelector(selectProfessions);
  const filters = useAppSelector(selectReplayFilters, sameFilters);
  const managers = useAppSelector(selectManagers, sameManagers);
  const dictionary = equipment ?? NO_EQUIPMENT;
  // Keyed on the frame, not the playhead: ticks inside one frame keep the same view.
  const base = useMemo(
    () => (replaying ? buildReplayView(frame, liveFloors, professions, dictionary, managerColors, filters) : null),
    [replaying, frame, liveFloors, professions, dictionary, managerColors, filters],
  );
  const floorLabels = useMemo(() => new Map(liveFloors.map((floor) => [floor.floorId, floor.label])), [liveFloors]);
  // The rail lists every on-site row, so it reads the frame unfiltered.
  const lists = useMemo(
    () => ({
      professionRows: replayWorkforceRows(frame, professions, floorLabels),
      managerRows: replayManagerRows(frame, managers),
      equipmentRows: replayEquipmentRows(frame, dictionary),
    }),
    [frame, professions, floorLabels, managers, dictionary],
  );
  const safetyRows = useMemo(() => replaySafetyRows(safety, floorLabels), [safety, floorLabels]);
  // Its own identity, so the rail keeps it while only the toasts (`activeEvents`) change.
  const rail = useMemo(() => ({ ...lists, safetyRows }), [lists, safetyRows]);
  return useMemo(() => base && { ...base, activeEvents, rail }, [base, activeEvents, rail]);
}
